import React, { useState, useEffect, useRef } from "react";
import { DragDropContext, Draggable } from "react-beautiful-dnd";
import { StrictModeDroppable as Droppable } from "./helpers/StrictModeDroppable";
import { filterWithGetAllTasks, updateTaskMilestone, updateTaskAssignee, bulkUpdateTaskMilestone, getProjectList } from "../../services/chartService";
import KanbanModal from "./KanbanModal";
import Select from "react-select";
import { imageIdGenerate } from "../../utils/custom";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Link } from "react-router-dom";
import { fetchProjectSprints } from "../../services/managerWorkListService";
import moment from "moment";
import { kanbanTaskEndDateThreshold, milestoneStatusIdConstants } from "../../utils/constants";
import './KanbanBoard.css';

const onDragEnd = async (result, columns, setColumns) => {
  if (!result.destination) return;
  const { source, destination } = result;
  const task = columns[source.droppableId].items[source.index];

  if (source.droppableId !== destination.droppableId) {
    const milestoneMap = {
      todo: 1,
      in_progress: 2,
      dev_done: 7,
      ready_for_qa: 3,
      qa_in_progress: 4,
      qa_done: 8,
      ready_for_client_review: 5,
      approved: 6,
    };
    const updateData = {
      milestone_id: milestoneMap[destination.droppableId] || [],
    };
    const sourceColumn = columns[source.droppableId];
    const destColumn = columns[destination.droppableId];
    const sourceItems = [...sourceColumn.items];
    const destItems = [...destColumn.items];
    const [removed] = sourceItems.splice(source.index, 1);
    removed.milestoneId = milestoneMap[destination.droppableId];
    destItems.splice(destination.index, 0, removed);
    setColumns({
      ...columns,
      [source.droppableId]: {
        ...sourceColumn,
        items: sourceItems,
        count: sourceItems.length ?? 0,
      },
      [destination.droppableId]: {
        ...destColumn,
        items: destItems,
        count: destItems.length ?? 0,
      },
    });
    const response = await updateTaskMilestone(task.id, updateData);
    if (response?.success === true) {
      window.toastr.success(response?.message);
    } else if (response?.success === false) {
      window.toastr.error(response?.message);
      const column = columns[source.droppableId];
      const copiedItems = [...column.items];
      const [removed] = copiedItems.splice(source.index, 1);
      copiedItems.splice(destination.index, 0, removed);
      setColumns({
        ...columns,
        [source.droppableId]: {
          ...column,
          items: copiedItems,
          count: copiedItems.length ?? 0,
        },
      });
    }
  }
};

const KanbanBoard = () => {
  const [todoTask, setTodoTask] = useState([]);
  const [inProgressTask, setInProgressTask] = useState([]);
  const [devDoneTask, setDevDoneTask] = useState([]);
  const [readyForQATask, setReadyForQATask] = useState([]);
  const [qaInProgressTask, setQAInProgressTask] = useState([]);
  const [qaDoneTask, setQADoneTask] = useState([]);
  const [readyForClientReviewTask, setReadyForClientReviewTask] = useState([]);
  const [approvedTask, setApprovedTask] = useState([]);
  const [filterValue, setFilterValue] = useState("");
  const [filterList, setFilterList] = useState();
  const [disable, setDisable] = useState(true);
  const [lastSelectedProject, setLastSelectedProject] = useState();
  const [sprints, setSprints] = useState();
  const [lastSelectedSprint, setLastSelectedSprint] = useState();
  const [hasNoSprints, setHasNoSprints] = useState(false);

  const handleFilterList = (obj) => {
    setFilterList(obj);
  };

  const [currentTodoTask, setCurrentTodoTask] = useState();
  const [currentInProgressTask, setCurrentInProgressTask] = useState();
  const [currentDevDoneTask, setCurrentDevDoneTask] = useState();
  const [currentReadyForQATask, setCurrentReadyForQATask] = useState();
  const [currentQAInProgessTask, setCurrentQAInProgessTask] = useState();
  const [currentQADoneTask, setCurrentQADoneTask] = useState();
  const [currentReadyForClientReviewTask, setCurrentReadyForClientReviewTask] = useState();
  const [currentApprovedTask, setCurrentApprovedTask] = useState();
  const [projectList, setProjectList] = useState([]);
  const [selectedProjectMembers, setSelectedProjectMembers] = useState([]);
  const [assigneeDropdownTaskId, setAssigneeDropdownTaskId] = useState(null);
  const assigneeDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (assigneeDropdownRef.current && !assigneeDropdownRef.current.contains(e.target)) {
        setAssigneeDropdownTaskId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleAssigneeChange = async (taskId, newAssignee, columnId) => {
    setAssigneeDropdownTaskId(null);
    const prevColumns = { ...columns };
    setColumns((prev) => {
      const updatedItems = prev[columnId].items.map((item) => {
        if (item.id === taskId) {
          return {
            ...item,
            assignee_id: newAssignee.id,
            assigneeFirstName: newAssignee.first_name,
            assigneeLastName: newAssignee.last_name,
            assignee_image_url: null,
          };
        }
        return item;
      });
      return {
        ...prev,
        [columnId]: { ...prev[columnId], items: updatedItems },
      };
    });

    const response = await updateTaskAssignee(taskId, { assignee_id: newAssignee.id });
    if (response?.success === true) {
      window.toastr.success(response?.message);
    } else {
      window.toastr.error(response?.message || "Failed to update assignee");
      setColumns(prevColumns);
    }
  };
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [bulkMilestone, setBulkMilestone] = useState(null);
  const [bulkUpdating, setBulkUpdating] = useState(false);

  useEffect(() => {
    const fetchAllProjectList = async () => {
      const fetchProjectList = await getProjectList();
      setProjectList(fetchProjectList?.data || []);
    };
    fetchAllProjectList();
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    setColumns((prevColumns) => ({
      ...prevColumns,
      todo: {
        ...prevColumns.todo,
        items: todoTask || [],
        count: (todoTask || []).length,
      },
      in_progress: {
        ...prevColumns.in_progress,
        items: inProgressTask || [],
        count: (inProgressTask || []).length,
      },
      dev_done: {
        ...prevColumns?.dev_done,
        items: devDoneTask || [],
        count: (devDoneTask || []).length,
      },
      ready_for_qa: {
        ...prevColumns.ready_for_qa,
        items: readyForQATask || [],
        count: (readyForQATask || []).length,
      },
      qa_in_progress: {
        ...prevColumns.qa_in_progress,
        items: qaInProgressTask || [],
        count: (qaInProgressTask || []).length,
      },
      qa_done: {
        ...prevColumns.qa_done,
        items: qaDoneTask || [],
        count: (qaDoneTask || []).length,
      },
      ready_for_client_review: {
        ...prevColumns.ready_for_client_review,
        items: readyForClientReviewTask || [],
        count: (readyForClientReviewTask || []).length,
      },
      approved: {
        ...prevColumns.approved,
        items: approvedTask || [],
        count: (approvedTask || []).length,
      },
    }));
  }, [todoTask, inProgressTask, devDoneTask, readyForQATask, qaInProgressTask, qaDoneTask, readyForClientReviewTask, approvedTask]);

  useEffect(() => {
    const lastSelectedProject = localStorage.getItem("lastSelectedProject");
    if (lastSelectedProject) {
      const parsedSelectedProject = JSON.parse(localStorage.getItem("lastSelectedProject"));
      handleProjectChange(parsedSelectedProject);
    }
  }, []);

  const renderProjectOptions = () => {
    return projectList.map((project) => ({
      label: project.Project.name,
      value: project.Project.id,
      members: project.Members,
    }));
  };

  const handleProjectChange = (selectedOption) => {
    // selectedOption?.map((item) => console.log(item.value));
    setLastSelectedProject(selectedOption);
    setLastSelectedSprint("");
    setHasNoSprints(false);
    localStorage.setItem("lastSelectedProject", JSON.stringify(selectedOption));
    const projectId = selectedOption?.value;
    setSelectedProjectMembers(selectedOption?.members);
    setFilterList({ worklist_id: projectId });
    setDisable(!projectId);
  };

  useEffect(() => {
    let flag = true;
    const fetchAllTasks = async (filterList) => {
      filterWithGetAllTasks(filterList)
        .then((response) => {
          if (flag) {
            handleAllTasks(response);
          }
        })
        .catch((error) => {
          console.log(error);
        });
    };

    // Sprint projects need SprintId; miscellaneous / no-sprint projects load by worklist_id only
    if (filterList?.SprintId || (filterList?.worklist_id && hasNoSprints)) {
      fetchAllTasks(filterList);
    } else {
      handleAllTasks({});
    }

    return () => {
      flag = false;
    };
  }, [filterList, hasNoSprints]);

  const refreshTasks = async () => {
    try {
      if (filterList?.SprintId || (filterList?.worklist_id && hasNoSprints)) {
        const response = await filterWithGetAllTasks(filterList);
        handleAllTasks(response);
      } else {
        handleAllTasks({});
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    let flag = true;
    const fetchSprints = () => {
      const projectId = JSON.parse(localStorage.getItem("lastSelectedProject")).value;
      const selectedProjectId = lastSelectedProject?.value ? lastSelectedProject.value : projectId;
      fetchProjectSprints(selectedProjectId)
        .then((data) => {
          if (flag) {
            setSprints(data?.data);
            const activeSprints = data?.data?.active_sprints || [];
            const inactiveSprints = data?.data?.inactive_sprints || [];
            if (activeSprints.length > 0) {
              const ACTIVE_SPRINT_AT = 0;
              setHasNoSprints(false);
              // to set the current sprint by default according to selected project
              setFilterList({ worklist_id: selectedProjectId, SprintId: activeSprints[ACTIVE_SPRINT_AT]["value"] });
              handleSprintChange(activeSprints[ACTIVE_SPRINT_AT]);
            } else if (inactiveSprints.length > 0) {
              const INACTIVE_SPRINT_AT = 0;
              setHasNoSprints(false);
              setFilterList({ worklist_id: selectedProjectId, SprintId: inactiveSprints[INACTIVE_SPRINT_AT]["value"] });
              // to set the current sprint by default according to selected project
              handleSprintChange(inactiveSprints[INACTIVE_SPRINT_AT]);
            } else {
              // Miscellaneous / no-sprint projects: load all tasks without SprintId
              setHasNoSprints(true);
              setLastSelectedSprint("");
              setFilterList({ worklist_id: selectedProjectId });
            }
          }
        })
        .catch((error) => {
          console.log(error);
        });
    };
    if (lastSelectedProject) {
      fetchSprints();
    }

    return () => {
      flag = false;
    };

    // eslint-disable-next-line
  }, [lastSelectedProject]);

  const renderSprintOptions = () => {
    const group = [];
    if (sprints?.active_sprints && sprints?.inactive_sprints) {
      const active = {
        label: "Active Sprints",
        options: [],
      };
      const inactive = {
        label: "Inactive Sprints",
        options: [],
      };

      for (let i in sprints) {
        if (i === "active_sprints") {
          sprints[i].forEach((sprint) => {
            active.options.push({
              label: sprint.label,
              value: sprint.value,
            });
          });
        }
        if (i === "inactive_sprints") {
          sprints[i].forEach((sprint) => {
            inactive.options.push({
              label: sprint.label,
              value: sprint.value,
            });
          });
        }
      }

      group.push(active);
      group.push(inactive);
    }
    return group;
  };

  const handleSprintChange = (selectedOption) => {
    if (selectedOption.value) {
      setLastSelectedSprint(selectedOption);
      setFilterList((prev) => ({
        ...prev,
        worklist_id: prev?.worklist_id ?? lastSelectedProject?.value,
        SprintId: selectedOption.value,
      }));
    }
  };

  function handleAllTasks(response) {
    setTodoTask(response?.data?.todo);
    setInProgressTask(response?.data?.in_progress);
    setDevDoneTask(response?.data?.dev_done);
    setReadyForQATask(response?.data?.ready_for_qa);
    setQAInProgressTask(response?.data?.qa_in_progress);
    setQADoneTask(response?.data?.qa_done);
    setReadyForClientReviewTask(response?.data?.ready_for_client_review);
    setApprovedTask(response?.data?.approved);

    setCurrentTodoTask(response?.data?.todo);
    setCurrentInProgressTask(response?.data?.in_progress);
    setCurrentDevDoneTask(response?.data?.dev_done);
    setCurrentReadyForQATask(response?.data?.ready_for_qa);
    setCurrentQAInProgessTask(response?.data?.qa_in_progress);
    setCurrentQADoneTask(response?.data?.qa_done);
    setCurrentReadyForClientReviewTask(response?.data?.ready_for_client_review);
    setCurrentApprovedTask(response?.data?.approved);

    setSelectedTaskIds([]);
    setBulkMilestone(null);
  }

  const taskStatus = {
    todo: {
      name: "Todo",
      items: todoTask || [],
      id: 1,
      count: (todoTask || []).length ?? 0,
    },
    in_progress: {
      name: "In Progress",
      items: inProgressTask || [],
      id: 2,
      count: (inProgressTask || []).length ?? 0,
    },
    dev_done: {
      name: "Dev Done",
      items: devDoneTask || [],
      id: 7,
      count: (devDoneTask || []).length ?? 0,
    },
    ready_for_qa: {
      name: "Ready for QA",
      items: readyForQATask || [],
      id: 3,
      count: (readyForQATask || []).length ?? 0,
    },
    qa_in_progress: {
      name: "QA in Progress",
      items: qaInProgressTask || [],
      id: 4,
      count: (qaInProgressTask || []).length ?? 0,
    },
    qa_done: {
      name: "QA Done",
      items: qaDoneTask || [],
      id: 8,
      count: (qaDoneTask || []).length ?? 0,
    },
    ready_for_client_review: {
      name: "Ready for Client Review",
      item: readyForClientReviewTask || [],
      id: 5,
      count: (readyForClientReviewTask || []).length ?? 0,
    },
    approved: {
      name: "Approved",
      items: approvedTask || [],
      id: 6,
      count: (approvedTask || []).length ?? 0,
    },
  };

  const [columns, setColumns] = useState(taskStatus);

  const renderBulkMilestoneOptions = () => {
    return Object.values(taskStatus).map((column) => ({
      label: column.name,
      value: column.id,
    }));
  };

  const toggleCardSelection = (event, taskId) => {
    event.stopPropagation();
    setSelectedTaskIds((prev) => (prev.includes(taskId) ? prev.filter((id) => id !== taskId) : [...prev, taskId]));
  };

  const clearSelection = () => {
    setSelectedTaskIds([]);
    setBulkMilestone(null);
  };

  const handleBulkMilestoneUpdate = async () => {
    if (!bulkMilestone?.value || selectedTaskIds.length === 0) {
      return;
    }
    setBulkUpdating(true);
    const response = await bulkUpdateTaskMilestone({ task_ids: selectedTaskIds, milestone_id: bulkMilestone.value });
    setBulkUpdating(false);
    if (response?.success === true) {
      window.toastr.success(response?.message ?? "Task milestone updated successfully");
      await refreshTasks();
    } else {
      window.toastr.error(response?.message ?? "Cannot update task milestones");
    }
  };

  const handleFilterChange = (e) => {
    const inputValue = e.target.value.toLowerCase(); // Convert input value to lowercase for case-insensitive matching
    // Filter tasks in each column based on the input value
    const filteredTodo = currentTodoTask ? currentTodoTask.filter((task) => (task?.name?.toLowerCase().includes(inputValue)) || task?.unique_task_no?.toLowerCase().includes(inputValue)) : [];
    const filteredInProgress = currentInProgressTask ? currentInProgressTask.filter((task) => (task?.name?.toLowerCase().includes(inputValue)) || task?.unique_task_no?.toLowerCase().includes(inputValue)) : [];
    const filteredDevDone = currentDevDoneTask ? currentDevDoneTask.filter((task) => (task?.name?.toLowerCase().includes(inputValue)) || task?.unique_task_no?.toLowerCase().includes(inputValue)) : [];
    const filteredReadyForQA = currentReadyForQATask ? currentReadyForQATask.filter((task) => (task?.name?.toLowerCase().includes(inputValue)) || task?.unique_task_no?.toLowerCase().includes(inputValue)) : [];
    const filteredQAInProgressTask = currentQAInProgessTask ? currentQAInProgessTask.filter((task) => (task?.name?.toLowerCase().includes(inputValue)) || task?.unique_task_no?.toLowerCase().includes(inputValue)) : [];
    const filteredQADone = currentQADoneTask ? currentQADoneTask.filter((task) => (task?.name?.toLowerCase().includes(inputValue)) || task?.unique_task_no?.toLowerCase().includes(inputValue)) : [];
    const filteredReadyForClientReviewTask = currentReadyForClientReviewTask ? currentReadyForClientReviewTask.filter((task) => (task?.name?.toLowerCase().includes(inputValue)) || task?.unique_task_no?.toLowerCase().includes(inputValue)) : [];
    const filteredApproved = currentApprovedTask ? currentApprovedTask.filter((task) => (task?.name?.toLowerCase().includes(inputValue)) || task?.unique_task_no?.toLowerCase().includes(inputValue)) : [];

    // Update the state for each column with the filtered tasks or reset to original when inputValue is empty
    setTodoTask(inputValue === "" ? currentTodoTask : filteredTodo);
    setInProgressTask(inputValue === "" ? currentInProgressTask : filteredInProgress);
    setDevDoneTask(inputValue === "" ? currentDevDoneTask : filteredDevDone);
    setReadyForQATask(inputValue === "" ? currentReadyForQATask : filteredReadyForQA);
    setQAInProgressTask(inputValue === "" ? currentQAInProgessTask : filteredQAInProgressTask);
    setQADoneTask(inputValue === "" ? currentQADoneTask : filteredQADone);
    setReadyForClientReviewTask(inputValue === "" ? currentReadyForClientReviewTask : filteredReadyForClientReviewTask);
    setApprovedTask(inputValue === "" ? currentApprovedTask : filteredApproved);

    // Update the filterValue state with the current input value
    setFilterValue(inputValue);
  };

  const getTaskStatusColor = ({ endDate, milestone }) => {
    const today = moment().utcOffset('+05:30');
    const taskEndDate = moment.utc(endDate, 'YYYY-MM-DD').utcOffset('+05:30');

    if (today.isAfter(taskEndDate, 'day')) {
      if (milestone === milestoneStatusIdConstants.READY_FOR_CLIENT_REVIEW || milestone === milestoneStatusIdConstants.APPROVED) return '';
      return 'text-danger'; // Red
    }

    const daysUntilDue = taskEndDate.diff(today, 'days');
    if(milestone !== milestoneStatusIdConstants.READY_FOR_CLIENT_REVIEW && milestone !== milestoneStatusIdConstants.APPROVED){
      if (daysUntilDue <= kanbanTaskEndDateThreshold.urgent) {
        return 'light-orange-text'; // Light Orange
      } else if (daysUntilDue <= kanbanTaskEndDateThreshold.warning) {
        return 'light-yellow-text'; // Light Yellow
      } else if (daysUntilDue > kanbanTaskEndDateThreshold.warning) {
        return 'text-success'; // Light Green
      }
    }
      
    return '';
  };

  return (
    <>
      {selectedTaskIds.length > 0 && (
        <div className="d-flex align-items-center bg-light-primary rounded p-4 mb-4 mx-5">
          <span className="fw-bold text-gray-800 me-4">{selectedTaskIds.length} task(s) selected</span>
          <div style={{ minWidth: "220px" }} className="me-3">
            <Select
              options={renderBulkMilestoneOptions()}
              isMulti={false}
              placeholder="Move to milestone..."
              value={bulkMilestone}
              onChange={setBulkMilestone}
            />
          </div>
          <button
            type="button"
            className="btn btn-sm btn-primary me-2"
            disabled={!bulkMilestone?.value || bulkUpdating}
            onClick={handleBulkMilestoneUpdate}
          >
            {bulkUpdating ? "Updating..." : "Move"}
          </button>
          <button type="button" className="btn btn-sm btn-light" onClick={clearSelection} disabled={bulkUpdating}>
            Clear
          </button>
        </div>
      )}
      <div className="row mb-4">
        <div className="col-2 px-5 mt-8">
          <div className="input-group">
            <input type="text" className="form-control" placeholder="Search by task name..." value={filterValue} onChange={handleFilterChange} />
          </div>
        </div>
        <div className="col-3 px-5 ms-auto">
          <div className="d-flex flex-column">
            <div className="d-flex">
              <div className="input-group me-2 flex-column">
                <label className="form-label">Select Project </label>
                <Select options={renderProjectOptions()} value={lastSelectedProject} onChange={handleProjectChange} isMulti={false} placeholder="Select Project..." />
              </div>

              <div className="input-group me-4 flex-column">
                <label className="form-label"> Sprint </label>
                <Select
                  options={renderSprintOptions()}
                  value={lastSelectedSprint}
                  onChange={handleSprintChange}
                  groupBy="label"
                  isMulti={false}
                  placeholder={hasNoSprints ? "No sprint (all tasks)" : "Select Sprint..."}
                  isDisabled={hasNoSprints}
                />
              </div>
            </div>
            <div className="ms-auto">
              <KanbanModal handleFilterList={handleFilterList} setLastSelectedSprint={setLastSelectedSprint} selectedProjectMembers={selectedProjectMembers} filterList={filterList} disable={disable} />
            </div>
          </div>
        </div>
      </div>
      <div className="kanban-container">
        <div style={{ display: "flex", justifyContent: "center", height: "100%" }}>
          <DragDropContext onDragEnd={(result) => onDragEnd(result, columns, setColumns)}>
            {Object.entries(columns).map(([columnId, column], index) => {
              return (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                  key={columnId}
                >
                  <span className="fw-bold text-gray-900 fs-2">
                    {column?.name} ({column?.count})
                  </span>

                  <div className="px-5 py-5">
                    <Droppable droppableId={columnId} key={columnId}>
                      {(provided, snapshot) => {
                        return (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            data-id="_fixed_height"
                            className="kanban-board custom_scroll"
                            style={{
                              width: "250px",
                              height: "620px",
                              padding: 16,
                            }}
                          >
                            {column.items?.map((item, index) => {
                              return (
                                <Draggable key={item.id} draggableId={item?.id.toString()} index={index}>
                                  {(provided, snapshot) => {
                                    return (
                                      <div className={`kanban-item ${snapshot.isDragging ? "dragging" : ""}`} ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} 
                                      >
                                          <div className="kanban-item-select form-check form-check-sm form-check-custom form-check-solid">
                                            <input
                                              className="form-check-input"
                                              type="checkbox"
                                              checked={selectedTaskIds.includes(item?.id)}
                                              onClick={(e) => e.stopPropagation()}
                                              onChange={(e) => toggleCardSelection(e, item?.id)}
                                            />
                                          </div>
                                          <div className="d-flex align-items-center">
                                            <div className="d-flex flex-column align-items-start pe-6">
                                              <OverlayTrigger overlay={<Tooltip>{"Go to Task"}</Tooltip>} key={item?.id} placement={"top"}>
                                                <div
                                                  className="d-flex mb-1 text-primary"
                                                  onClick={() => {
                                                    localStorage.setItem("key", item.id);
                                                  }}
                                                >
                                                  {" "}
                                                  <span className="fw-bold fs-3">#</span>
                                                  <Link to={`/task/${item?.unique_task_no}`} className="fw-bold fs-4 hover">
                                                    {item?.unique_task_no}
                                                  </Link>
                                                </div>
                                              </OverlayTrigger>
                                              <span className="fw-normal text-gray-700 fs-4" style={{ wordBreak: "break-all" }}>
                                                {item?.name}
                                              </span>
                                            </div>
                                          </div>
                                        <hr className="text-gray-400 p-0" />
                                        <div style={{ marginLeft: 0 }} className="symbol-group symbol-hover flex-nowrap justify-content-between align-items-center">
                                          <div className={`d-flex align-items-center ${getTaskStatusColor({ endDate: item?.end_date, milestone: item?.milestoneId })}`}>{item?.end_date ? moment(item?.end_date, 'YYYY-MM-DD').format('DD MMM, YY') : ""} {(item?.taskActivityEndDate && item?.end_date !== item?.taskActivityEndDate) && <span>*</span>}</div>
                                          <div className="kanban-assignee-wrapper" style={{ position: "relative" }}>
                                            <OverlayTrigger overlay={<Tooltip>{item?.assignee_id ? `${item?.assigneeFirstName} ${item?.assigneeLastName}` : "Unassigned"} (click to change)</Tooltip>} key={item?.id} placement={"left"}>
                                              <div
                                                className="symbol symbol-35px symbol-circle user-image kanban-assignee-avatar"
                                                data-kt-initialized="1"
                                                role="button"
                                                tabIndex={0}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setAssigneeDropdownTaskId(assigneeDropdownTaskId === item.id ? null : item.id);
                                                }}
                                                onKeyDown={(e) => {
                                                  if (e.key === "Enter" || e.key === " ") {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setAssigneeDropdownTaskId(assigneeDropdownTaskId === item.id ? null : item.id);
                                                  }
                                                }}
                                              >
                                                <img alt="name" src={item?.assignee_id ? item?.assignee_image_url ?? `${process.env.PUBLIC_URL}/assets/media/avatars/300-${imageIdGenerate(item?.assignee_id)}.jpg` : `${process.env.PUBLIC_URL}/assets/media/avatars/blank.png`} />
                                              </div>
                                            </OverlayTrigger>
                                            {assigneeDropdownTaskId === item.id && (
                                              <div ref={assigneeDropdownRef} className="kanban-assignee-dropdown">
                                                <div className="kanban-assignee-dropdown-header">Assign to</div>
                                                <div className="kanban-assignee-dropdown-list">
                                                  {selectedProjectMembers?.length > 0 ? (
                                                    selectedProjectMembers.map((member) => (
                                                      <div
                                                        key={member.id}
                                                        role="button"
                                                        tabIndex={0}
                                                        className={`kanban-assignee-dropdown-item ${item.assignee_id === member.id ? "active" : ""}`}
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          handleAssigneeChange(item.id, member, columnId);
                                                        }}
                                                        onKeyDown={(e) => {
                                                          if (e.key === "Enter" || e.key === " ") {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            handleAssigneeChange(item.id, member, columnId);
                                                          }
                                                        }}
                                                      >
                                                        <div className="symbol symbol-25px symbol-circle me-2">
                                                          <img alt={member.first_name} src={`${process.env.PUBLIC_URL}/assets/media/avatars/300-${imageIdGenerate(member.id)}.jpg`} />
                                                        </div>
                                                        <span>{member.first_name} {member.last_name}</span>
                                                        {item.assignee_id === member.id && <span className="ms-auto text-primary">&#10003;</span>}
                                                      </div>
                                                    ))
                                                  ) : (
                                                    <div className="kanban-assignee-dropdown-item text-muted">No members available</div>
                                                  )}
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  }}
                                </Draggable>
                              );
                            })}
                            {provided.placeholder}
                          </div>
                        );
                      }}
                    </Droppable>
                  </div>
                </div>
              );
            })}
          </DragDropContext>
        </div>
      </div>
    </>
  );
};
export default KanbanBoard;
